import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);

  // Carregar carrinho do localStorage ou do servidor
  useEffect(() => {
    const token = localStorage.getItem('customer_token');
    const savedName = localStorage.getItem('customer_name');
    
    if (token && savedName) {
      setUser({ name: savedName, token });
      fetchCart(token);
    } else {
      // Carregar carrinho local para usuários não logados
      loadLocalCart();
    }
    setLoading(false);
  }, []);

  const loadLocalCart = () => {
    const localCart = localStorage.getItem('guest_cart');
    if (localCart) {
      setCart(JSON.parse(localCart));
    }
  };

  const saveLocalCart = (newCart) => {
    localStorage.setItem('guest_cart', JSON.stringify(newCart));
    setCart(newCart);
  };

  const fetchCart = async (token) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/customer/cart`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCart(response.data.cart || []);
    } catch (error) {
      console.error('Erro ao buscar carrinho:', error);
    }
  };

  const syncCartToServer = async (token) => {
    // Sincronizar carrinho local com o servidor ao fazer login
    const localCart = localStorage.getItem('guest_cart');
    if (localCart) {
      const cartItems = JSON.parse(localCart);
      for (const item of cartItems) {
        try {
          await axios.post(
            `${BACKEND_URL}/api/customer/cart/add`,
            { 
              project_id: item.project_id, 
              selected_services: item.selected_services 
            },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
        } catch (error) {
          console.error('Erro ao sincronizar item:', error);
        }
      }
      localStorage.removeItem('guest_cart');
    }
  };

  const login = async (token, name) => {
    localStorage.setItem('customer_token', token);
    localStorage.setItem('customer_name', name);
    setUser({ name, token });
    
    // Sincronizar carrinho local com servidor
    await syncCartToServer(token);
    await fetchCart(token);
  };

  const logout = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_name');
    setUser(null);
    setCart([]);
    loadLocalCart(); // Carregar carrinho local após logout
  };

  const addToCart = async (projectId, selectedServices, projectData) => {
    // Calcular preço total baseado nos serviços selecionados
    let totalPrice = 0;
    
    if (selectedServices.includes('arquitetonico')) {
      totalPrice += projectData.price;
    }
    if (selectedServices.includes('interiores')) {
      totalPrice += projectData.interiorPrice || 1618.50;
    }
    if (selectedServices.includes('rrt')) {
      totalPrice += projectData.rrtPrice || 1300.00;
    }
    
    // Se não estiver logado, adicionar ao carrinho local
    if (!user) {
      const newItem = {
        project_id: projectId,
        project_name: projectData.name,
        project_price: totalPrice,
        project_base_price: projectData.price,
        project_interior_price: projectData.interiorPrice || 1618.50,
        project_rrt_price: projectData.rrtPrice || 1300.00,
        selected_services: selectedServices,
        added_at: new Date().toISOString()
      };

      // Verificar se já existe
      const existingCart = [...cart];
      const exists = existingCart.find(item => item.project_id === projectId);
      if (exists) {
        throw new Error('Projeto já está no carrinho');
      }

      const newCart = [...existingCart, newItem];
      saveLocalCart(newCart);
      return true;
    }

    // Se estiver logado, adicionar ao servidor
    try {
      await axios.post(
        `${BACKEND_URL}/api/customer/cart/add`,
        { project_id: projectId, selected_services: selectedServices },
        { headers: { 'Authorization': `Bearer ${user.token}` } }
      );
      
      await fetchCart(user.token);
      return true;
    } catch (error) {
      throw error;
    }
  };

  const removeFromCart = async (projectId) => {
    // Se não estiver logado, remover do carrinho local
    if (!user) {
      const newCart = cart.filter(item => item.project_id !== projectId);
      saveLocalCart(newCart);
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/customer/cart/remove`,
        { project_id: projectId },
        { headers: { 'Authorization': `Bearer ${user.token}` } }
      );
      
      await fetchCart(user.token);
    } catch (error) {
      console.error('Erro ao remover do carrinho:', error);
    }
  };

  const clearCart = async () => {
    if (!user) {
      saveLocalCart([]);
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/customer/cart/clear`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      
      setCart([]);
    } catch (error) {
      console.error('Erro ao limpar carrinho:', error);
    }
  };

  const value = {
    user,
    loading,
    cart,
    login,
    logout,
    addToCart,
    removeFromCart,
    clearCart,
    refreshCart: () => user ? fetchCart(user.token) : loadLocalCart()
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
